/**
 * Birthday Utilities
 * Helper functions for birthday calculations and checks
 */

/**
 * Check if user's birthday is within the next 7 days or today
 * @param birthDate - User's birth date (YYYY-MM-DD format or Date string)
 * @returns Object with isBirthdaySoon, daysUntilBirthday, isToday
 */
export const checkBirthdayStatus = (birthDate: string | null | undefined): {
  isBirthdaySoon: boolean;
  daysUntilBirthday: number;
  isToday: boolean;
  isBirthdayWeek: boolean;
} => {
  if (!birthDate) {
    return {
      isBirthdaySoon: false,
      daysUntilBirthday: -1,
      isToday: false,
      isBirthdayWeek: false
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const birthDateObj = new Date(birthDate);
  const currentYear = today.getFullYear();
  
  // Get this year's birthday
  const thisYearBirthday = new Date(currentYear, birthDateObj.getMonth(), birthDateObj.getDate());
  thisYearBirthday.setHours(0, 0, 0, 0);

  // Get next year's birthday (in case birthday already passed this year)
  const nextYearBirthday = new Date(currentYear + 1, birthDateObj.getMonth(), birthDateObj.getDate());
  nextYearBirthday.setHours(0, 0, 0, 0);

  // Determine which birthday is next
  let nextBirthday = thisYearBirthday;
  if (thisYearBirthday < today) {
    nextBirthday = nextYearBirthday;
  }

  // Calculate days until birthday
  const diffTime = nextBirthday.getTime() - today.getTime();
  const daysUntilBirthday = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const isToday = daysUntilBirthday === 0;
  const isBirthdayWeek = daysUntilBirthday >= 0 && daysUntilBirthday <= 7;
  const isBirthdaySoon = isBirthdayWeek || isToday;

  return {
    isBirthdaySoon,
    daysUntilBirthday,
    isToday,
    isBirthdayWeek
  };
};

/**
 * Calculate time remaining until birthday in real-time
 * @param birthDate - User's birth date
 * @returns Object with days, hours, minutes, seconds until birthday
 */
export const calculateTimeUntilBirthday = (birthDate: string | null | undefined): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
} => {
  if (!birthDate) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalSeconds: 0
    };
  }

  const now = new Date();
  const birthDateObj = new Date(birthDate);
  const currentYear = now.getFullYear();
  
  // Get this year's birthday at midnight
  let nextBirthday = new Date(currentYear, birthDateObj.getMonth(), birthDateObj.getDate());
  nextBirthday.setHours(0, 0, 0, 0);

  // If birthday already passed this year, use next year
  if (nextBirthday < now) {
    nextBirthday = new Date(currentYear + 1, birthDateObj.getMonth(), birthDateObj.getDate());
    nextBirthday.setHours(0, 0, 0, 0);
  }

  // If it's the birthday day, set to end of day
  if (nextBirthday.getTime() === new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) {
    nextBirthday.setHours(23, 59, 59, 999);
  }

  const diffTime = nextBirthday.getTime() - now.getTime();
  const totalSeconds = Math.max(0, Math.floor(diffTime / 1000));

  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    totalSeconds
  };
};

